import { Injectable } from "@angular/core";
import { DisplayOptions } from "../models/filter-models";
import { DisplayType, EquipmentTierType } from "../models/enums";
import { CosmeticOptions } from "../models/filter-models";
import { FilterStoreService } from "./filter-store.service";
import { logEvent } from "../app.config";
import { poe1 } from "../../game";
import { Tier } from "../models/item-data-models";

@Injectable({
  providedIn: 'root'
})
export class CosmeticsGeneratorService {
  constructor(private filterStoreService: FilterStoreService) {
  }

  getCosmeticOptionsFromDisplayOptions(displayOptions: DisplayOptions, exampleName?: string, title?: string): { cosmeticOptions: CosmeticOptions; hidden: boolean } {
    let cosmeticOptions: CosmeticOptions;
    let hidden = false;
    const filter = this.filterStoreService.activeFilter();

    if (displayOptions.displayType === DisplayType.Custom) {
      if (displayOptions.cosmeticOptions) {
        cosmeticOptions = displayOptions.cosmeticOptions;
        hidden = displayOptions.hidden ?? false;
      } else {
        logEvent('Trace',
          'displayOptions.displayType is Custom but no cosmeticOptions set. Setting default.',
          'exampleName: ' + exampleName + ' - title: ' + title + ' - ' + JSON.stringify(displayOptions));
        cosmeticOptions = new CosmeticOptions();
      }
    } else if (displayOptions.displayType === DisplayType.Tier) {
      const tierDisplayOptions = filter.tierCosmetics.find(t => t.tier === displayOptions.displayTypeTier)?.displayOptions;
      cosmeticOptions = new CosmeticOptions(tierDisplayOptions?.cosmeticOptions!);
      hidden = displayOptions.hidden || (tierDisplayOptions?.hidden ?? false);
    } else if (displayOptions.displayType === DisplayType.DivCardTier) {
      const tierDisplayOptions = filter.divCardCosmeticsSameAsTierCosmetics
        ? filter.tierCosmetics.find(t => t.tier === displayOptions.displayTypeTier)?.displayOptions
        : filter.divCardTierCosmetics.find(t => t.tier === displayOptions.displayTypeTier)?.displayOptions;

      cosmeticOptions = new CosmeticOptions(tierDisplayOptions?.cosmeticOptions!);

      if (filter.divCardCosmeticsSameAsTierCosmetics && filter.divCardCosmeticsSameAsTierCosmeticsOverrides.displayOptions?.active) {
        this.copyOverrideCosmeticOptions(filter.divCardCosmeticsSameAsTierCosmeticsOverrides.displayOptions.cosmeticOptions!, cosmeticOptions);
      }

      hidden = displayOptions.hidden || (tierDisplayOptions?.hidden ?? false);
    } else if (displayOptions.displayType === DisplayType.EquipmentTier) {
      const tierDisplayOptions = filter.equipmentCosmeticsSameAsTierCosmetics
        ? filter.tierCosmetics.find(t => t.tier === displayOptions.displayTypeTier)?.displayOptions
        : filter.equipmentTierCosmetics.find(t => t.tier === displayOptions.displayTypeTier)?.displayOptions;

      cosmeticOptions = new CosmeticOptions(tierDisplayOptions?.cosmeticOptions!);

      if (filter.equipmentCosmeticsSameAsTierCosmetics && filter.equipmentCosmeticsSameAsTierCosmeticsOverrides.displayOptions?.active) {
        this.copyOverrideCosmeticOptions(filter.equipmentCosmeticsSameAsTierCosmeticsOverrides.displayOptions.cosmeticOptions!, cosmeticOptions);
      }

      if (displayOptions.displayTypeTierHighlightType === EquipmentTierType.MyEquipment && filter.myItemsStyleOverride?.displayOptions?.active) {
        this.copyOverrideCosmeticOptions(filter.myItemsStyleOverride.displayOptions.cosmeticOptions!, cosmeticOptions);
      }

      if (displayOptions.displayTypeTierHighlightType === EquipmentTierType.LevelingEquipment && filter.levelingItemsStyleOverride?.displayOptions?.active) {
        this.copyOverrideCosmeticOptions(filter.levelingItemsStyleOverride.displayOptions.cosmeticOptions!, cosmeticOptions);
      }

      if (displayOptions.displayTypeTierHighlightType === EquipmentTierType.SalvagingEquipment && filter.salvagingItemsStyleOverride?.displayOptions?.active) {
        this.copyOverrideCosmeticOptions(filter.salvagingItemsStyleOverride.displayOptions.cosmeticOptions!, cosmeticOptions);
      }

      if (displayOptions.displayTypeTierHighlightType === EquipmentTierType.IdentifiedEquipment && filter.identifiedItemsStyleOverride?.displayOptions?.active) {
        this.copyOverrideCosmeticOptions(filter.identifiedItemsStyleOverride.displayOptions.cosmeticOptions!, cosmeticOptions);
      }

      if (displayOptions.displayTypeTierHighlightType === EquipmentTierType.UnidentifiedEquipment && filter.unidentifiedItemsStyleOverride?.displayOptions?.active) {
        this.copyOverrideCosmeticOptions(filter.unidentifiedItemsStyleOverride.displayOptions.cosmeticOptions!, cosmeticOptions);
      }

      if (displayOptions.displayTypeTierHighlightType === EquipmentTierType.CraftingBaseEquipment && filter.craftingBasesStyleOverride?.displayOptions?.active) {
        this.copyOverrideCosmeticOptions(filter.craftingBasesStyleOverride.displayOptions.cosmeticOptions!, cosmeticOptions);
      }

      if (displayOptions.displayTypeTierHighlightType === EquipmentTierType.Gem && filter.gemStyleOverride?.displayOptions?.active) {
        this.copyOverrideCosmeticOptions(filter.gemStyleOverride.displayOptions.cosmeticOptions!, cosmeticOptions);
      }

      if (displayOptions.displayTypeTierHighlightType === EquipmentTierType.Map && filter.mapStyleOverride?.displayOptions?.active) {
        this.copyOverrideCosmeticOptions(filter.mapStyleOverride.displayOptions.cosmeticOptions!, cosmeticOptions);
      }

      if (displayOptions.displayTypeTierHighlightType === EquipmentTierType.Gold && filter.goldStyleOverride?.displayOptions?.active) {
        this.copyOverrideCosmeticOptions(filter.goldStyleOverride.displayOptions.cosmeticOptions!, cosmeticOptions);
        cosmeticOptions.alertSound = false;
      }

      if (displayOptions.displayTypeTierHighlightType === EquipmentTierType.Verisium && filter.verisiumStyleOverride?.displayOptions?.active) {
        this.copyOverrideCosmeticOptions(filter.verisiumStyleOverride.displayOptions.cosmeticOptions!, cosmeticOptions);
        cosmeticOptions.alertSound = false;
      }

      hidden = displayOptions.hidden || displayOptions.displayTypeTier === Tier.FHidden || (tierDisplayOptions?.hidden ?? false);
    } else if (displayOptions.displayType === DisplayType.ItemTypeOverrides && displayOptions.active) {
      cosmeticOptions = new CosmeticOptions();
      this.copyOverrideCosmeticOptions(displayOptions.cosmeticOptions!, cosmeticOptions);
    } else {
      cosmeticOptions = new CosmeticOptions();
      hidden = displayOptions.hidden ?? false;
    }

    return {
      cosmeticOptions: cosmeticOptions,
      hidden: hidden
    };
  }

  copyOverrideCosmeticOptions(overrideCosmeticOptions: CosmeticOptions, cosmeticOptions: CosmeticOptions) {
    if (overrideCosmeticOptions.textColor) {
      cosmeticOptions.textColor = overrideCosmeticOptions.textColor;
    }

    if (overrideCosmeticOptions.backgroundColor) {
      cosmeticOptions.backgroundColor = overrideCosmeticOptions.backgroundColor;
    }

    if (overrideCosmeticOptions.borderColor) {
      cosmeticOptions.borderColor = overrideCosmeticOptions.borderColor;
    }

    if (overrideCosmeticOptions.fontSize) {
      cosmeticOptions.fontSize = overrideCosmeticOptions.fontSize;
    }

    if (overrideCosmeticOptions.minimapIconShape && cosmeticOptions.minimapIconShape) {
      cosmeticOptions.minimapIconShape = overrideCosmeticOptions.minimapIconShape;
    }
  }
}
